import { useMemo, useState } from "react";
import * as ContextMenu from "@radix-ui/react-context-menu";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Archive,
  ChevronsDownUp,
  LayoutGrid,
  MoreHorizontal,
  Pencil,
  Plus,
  StickyNote,
  Trash2,
} from "lucide-react";
import type { WorkspaceItem } from "@hydra-notes/shared";
import { getData, useWorkspaceItems } from "@/data/store";
import { useUi } from "@/store/ui";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import { CARD_DRAG_TYPE, archiveDraggedCard, draggedCardId, moveCardToBoard, trashDraggedCard } from "./dnd";
import s from "./CardsSidebar.module.css";

export function CardsSidebar({ onClose }: { onClose?: () => void }) {
  const items = useWorkspaceItems();
  const activeBoard = useUi((state) => state.activeCardBoard);
  const mode = useUi((state) => state.cardMode);
  const toggleSidebar = useUi((state) => state.toggleTree);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");

  const boards = useMemo(
    () => items.filter((item) => item.type === "cardboard" && !item.archivedAt && !item.trashedAt),
    [items],
  );
  const liveCards = useMemo(
    () => items.filter((item) => item.type === "card" && !item.archivedAt && !item.trashedAt),
    [items],
  );
  const archiveCount = items.filter((item) => item.type === "card" && item.archivedAt && !item.trashedAt).length;
  const trashCount = items.filter((item) => item.type === "card" && item.trashedAt).length;

  const createBoard = () => {
    const title = draft.trim();
    if (!title) {
      setCreating(false);
      setDraft("");
      return;
    }
    const id = getData().workspace.createItem("cardboard", null, title);
    setDraft("");
    setCreating(false);
    useUi.getState().openCardBoard(id);
    toast.success(`Created board "${title}"`);
  };

  return (
    <div className={s.sidebar} aria-label="Cards navigation">
      <header className={s.header}>
        <span>Cards</span>
        <IconButton label="New card board" onClick={() => setCreating(true)}><Plus size={14} /></IconButton>
        <IconButton label="Hide sidebar" onClick={onClose ?? toggleSidebar}><ChevronsDownUp size={14} /></IconButton>
      </header>

      <nav className={s.nav}>
        <CardDestination
          active={mode === "cards" && activeBoard === "all"}
          icon={<LayoutGrid size={15} />}
          label="All cards"
          count={liveCards.length}
          onClick={() => useUi.getState().openCardBoard("all")}
          onCard={(id) => moveCardToBoard(id, null)}
        />

        <section className={s.section}>
          <h2>Boards</h2>
          {creating ? (
            <div className={s.createRow}>
              <StickyNote size={14} />
              <input
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={createBoard}
                onKeyDown={(event) => {
                  if (event.key === "Enter") createBoard();
                  if (event.key === "Escape") {
                    setDraft("");
                    setCreating(false);
                  }
                }}
                placeholder="Board name"
              />
            </div>
          ) : null}
          {boards.map((board) => (
            <BoardRow
              key={board.id}
              board={board}
              active={mode === "cards" && activeBoard === board.id}
              count={liveCards.filter((card) => card.parentId === board.id).length}
            />
          ))}
          {!boards.length && !creating ? <p className={s.boardHint}>Create boards to group related Cards.</p> : null}
        </section>
      </nav>

      <footer className={s.footer}>
        <CardDestination
          active={mode === "archive"}
          icon={<Archive size={14} />}
          label="Archive"
          count={archiveCount}
          onClick={() => useUi.getState().setCardMode("archive")}
          onCard={archiveDraggedCard}
        />
        <CardDestination
          active={mode === "trash"}
          icon={<Trash2 size={14} />}
          label="Trash"
          count={trashCount}
          onClick={() => useUi.getState().setCardMode("trash")}
          onCard={trashDraggedCard}
        />
      </footer>
    </div>
  );
}

function BoardRow({ board, active, count }: { board: WorkspaceItem; active: boolean; count: number }) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(board.title);
  const [dragOver, setDragOver] = useState(false);

  const commitRename = () => {
    const next = title.trim();
    if (next) getData().workspace.rename(board.id, next);
    else setTitle(board.title);
    setRenaming(false);
  };

  const deleteBoard = () => {
    if (!confirm(`Delete the board "${board.title || "Untitled"}"? Its Cards will move to All cards.`)) return;
    const workspace = getData().workspace;
    for (const card of workspace.getSnapshot()) {
      if (card.type !== "card" || card.parentId !== board.id) continue;
      if (card.trashedAt || card.archivedAt) workspace.reparent(card.id, null);
      else workspace.move(card.id, null);
    }
    workspace.trash(board.id);
    if (useUi.getState().activeCardBoard === board.id) useUi.getState().openCardBoard("all");
    toast.success(`Deleted board "${board.title || "Untitled"}". Its Cards moved to All cards.`);
  };

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <div
          className={[s.boardRow, active ? s.active : "", dragOver ? s.dropActive : ""].join(" ")}
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes(CARD_DRAG_TYPE)) return;
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            const id = draggedCardId(event.dataTransfer);
            if (id) moveCardToBoard(id, board.id);
          }}
        >
          {renaming ? (
            <div className={s.renameRow}>
              <StickyNote size={14} />
              <input
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                onBlur={commitRename}
                onKeyDown={(event) => {
                  if (event.key === "Enter") commitRename();
                  if (event.key === "Escape") {
                    setTitle(board.title);
                    setRenaming(false);
                  }
                }}
              />
            </div>
          ) : (
            <button type="button" className={s.boardOpen} onClick={() => useUi.getState().openCardBoard(board.id)}>
              <StickyNote size={14} />
              <span>{board.title || "Untitled"}</span>
              <small>{count}</small>
            </button>
          )}
          {!renaming ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <span className={s.menuTrigger}><IconButton label="Card board actions"><MoreHorizontal size={13} /></IconButton></span>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="hn-menu" side="right" align="start" sideOffset={5}>
                  <DropdownMenu.Item className="hn-menu-item" onSelect={() => setRenaming(true)}><Pencil /> Rename</DropdownMenu.Item>
                  <DropdownMenu.Item className="hn-menu-item danger" onSelect={deleteBoard}><Trash2 /> Delete board</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : null}
        </div>
      </ContextMenu.Trigger>
      {!renaming ? (
        <ContextMenu.Portal>
          <ContextMenu.Content className="hn-menu">
            <ContextMenu.Item className="hn-menu-item" onSelect={() => setRenaming(true)}><Pencil /> Rename</ContextMenu.Item>
            <ContextMenu.Item className="hn-menu-item danger" onSelect={deleteBoard}><Trash2 /> Delete board</ContextMenu.Item>
          </ContextMenu.Content>
        </ContextMenu.Portal>
      ) : null}
    </ContextMenu.Root>
  );
}

function CardDestination({
  active,
  icon,
  label,
  count,
  onClick,
  onCard,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  count: number;
  onClick: () => void;
  onCard: (cardId: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <button
      type="button"
      className={[s.destination, active ? s.active : "", dragOver ? s.dropActive : ""].join(" ")}
      onClick={onClick}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes(CARD_DRAG_TYPE)) return;
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);
        const id = draggedCardId(event.dataTransfer);
        if (id) onCard(id);
      }}
    >
      {icon}<span>{label}</span>{count ? <small>{count}</small> : null}
    </button>
  );
}
